import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, inviteUser, updateUserStatus } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Plus, CheckCircle, XCircle } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { USER_ROLE, formatDate, initials, label } from '../../constants/garmentCodes';

const PERMISSIONS = ['Order placement', 'Receipt confirmation', 'Document management'];
const EMPTY_FORM = { name: '', email: '', mobileNumber: '', role: '5', permissions: [] };
const inputCls = 'w-full mt-1.5 px-3 py-2.5 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0]';

export default function BrandTeam() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user.users);
  const loading = useSelector((state) => state.user.loading);
  const brandId = useSelector((state) => state.brand.profile?.id);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    dispatch(getAllUsers({ brandId }));
  }, [dispatch, brandId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const togglePerm = (p) => setForm((f) => ({
    ...f,
    permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p],
  }));

  const invite = (e) => {
    e.preventDefault();
    dispatch(
      inviteUser({
        payload: {
          name: form.name.trim(),
          email: form.email.trim(),
          mobileNumber: form.mobileNumber || null,
          role: Number(form.role),
          brandIds: brandId ? [brandId] : [],
          permissions: form.permissions,
        },
      }),
    );
    setForm(EMPTY_FORM);
    setShowInvite(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Brand Team" subtitle={`${users.length} members`} actions={<Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setShowInvite((v) => !v)}>Invite member</Button>} />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {showInvite && (
          <form onSubmit={invite} className="bg-white rounded-xl border border-[#E5E3DE] p-6 space-y-4">
            <h3 className="font-semibold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>Invite team member</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#5C5A54]">Full name *</label>
                <input required value={form.name} onChange={set('name')} placeholder="Name" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5C5A54]">Email *</label>
                <input required type="email" value={form.email} onChange={set('email')} placeholder="email@brand.com" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5C5A54]">Role *</label>
                <select value={form.role} onChange={set('role')} className={inputCls}>
                  <option value="4">Brand Owner / Administrator</option>
                  <option value="5">Brand Operations User</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5C5A54]">Mobile</label>
                <input value={form.mobileNumber} onChange={set('mobileNumber')} placeholder="+91 ..." className={inputCls} />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#5C5A54] mb-2">Additional permissions</p>
              <div className="flex gap-4 flex-wrap">
                {PERMISSIONS.map((p) => (
                  <label key={p} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#3D52A0]"
                      checked={form.permissions.includes(p)}
                      onChange={() => togglePerm(p)}
                    />
                    <span className="text-xs text-[#5C5A54]">{p}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowInvite(false)}>Cancel</Button>
              <Button type="submit" variant="primary" size="sm">Send invitation</Button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="BrandTeam"
            columns={[
              { id: 'member', label: 'MEMBER', align: 'left' },
              { id: 'role', label: 'ROLE', align: 'left' },
              { id: 'lastLogin', label: 'LAST LOGIN', align: 'left' },
              { id: '2fa', label: '2FA', align: 'left' },
              { id: 'invite', label: 'INVITE', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'action', label: '', align: 'center', disableFilter: true },
            ]}
            rows={users.map((u) => {
              const active = u.status !== 'Suspended';
              return {
                id: u.id,
                rColumns: [
                  <div key="m" label={u.name || ''} className={`flex items-center gap-3 w-full h-full px-2 py-1 ${u.__optimistic ? 'opacity-60' : ''}`}>
                    <div className="w-8 h-8 rounded-full bg-[#1E2A3B] flex items-center justify-center shrink-0"><span className="text-[10px] font-bold text-white">{u.avatar || initials(u.name)}</span></div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1915] truncate">{u.name}</p>
                      <p className="text-xs text-[#9B9893] truncate">{u.email}</p>
                    </div>
                  </div>,
                  <div key="r" label={label(USER_ROLE, u.role)} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{label(USER_ROLE, u.role)}</div>,
                  <div key="l" label={u.lastLogin || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#9B9893]">{formatDate(u.lastLogin)}</div>,
                  <div key="2" label={u.twoFactor ? 'Enabled' : 'Off'} className="flex items-center w-full h-full px-2 py-1">
                    {u.twoFactor ? <CheckCircle size={14} className="text-[#1A6B3E]" /> : <XCircle size={14} className="text-[#C9C7C2]" />}
                  </div>,
                  <div key="i" label={u.invite || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{u.invite || '—'}</div>,
                  <div key="s" label={u.status || ''} className="flex items-center w-full h-full px-2 py-1"><Badge variant={active ? 'green' : 'red'}>{u.status || 'Active'}</Badge></div>,
                  <div key="a" label="" className="flex items-center justify-center w-full h-full px-2 py-1">
                    {!u.__optimistic && (
                      <Button variant="ghost" size="xs" onClick={() => dispatch(updateUserStatus({ id: u.id, status: active ? 'Suspended' : 'Active' }))}>
                        {active ? 'Suspend' : 'Reactivate'}
                      </Button>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && users.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No team members yet</p>}
        </div>
      </div>
    </div>
  );
}
